type NativeField = {
  id: string; name?: string; type: string; hidden?: boolean;
  value?: unknown; text?: unknown; defaultValue?: unknown;
};

export const isHiddenFormField = (field: NativeField) =>
  field.type === 'hidden' || field.hidden === true;

export const getHiddenFormValues = (fields: NativeField[]) =>
  Object.fromEntries(fields.filter(isHiddenFormField).map(field => [
    field.id, field.value ?? field.defaultValue ?? field.text ?? '',
  ]));

export function reconcileFormValues(
  fields: NativeField[], response: any, submitted: Record<string, any>,
  newer: Record<string, any> = {}
): Record<string, any> {
  const server = response?.collectedData || {};
  const draft = response?.collectedDataMode === 'authoritative'
    ? { ...server, ...newer } : { ...server, ...submitted, ...newer };
  if (!['authoritative', 'preserveDraft'].includes(response?.collectedDataMode)) {
    return { ...draft, ...getHiddenFormValues(fields) };
  }
  return {
    ...Object.fromEntries(fields.filter(field => !isHiddenFormField(field) && field.type !== 'info')
      .filter(field => Object.prototype.hasOwnProperty.call(draft, field.id))
      .map(field => [field.id, draft[field.id]])),
    ...getHiddenFormValues(fields),
  };
}

export function formScalar(value: any): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(formScalar).filter(v => v !== '').join(',');
  if (typeof value === 'object') {
    return formScalar(value.value ?? value.key ?? value.id ?? value.text ??
      value.display ?? value.displayName ?? value.name ?? value.label);
  }
  return String(value);
}

export function serializeFormValues(fields: NativeField[], values: Record<string, any>): string {
  const hidden = getHiddenFormValues(fields);
  return fields.filter(field => field.type !== 'info').map(field => {
    const byName = field.name ? values[field.name] : undefined;
    const raw = isHiddenFormField(field) ? hidden[field.id] :
      byName === '' || byName === undefined || byName === null ? values[field.id] ?? '' : byName;
    const value = field.type === 'file' && typeof raw !== 'string' &&
      !(Array.isArray(raw) && raw.every(item => typeof item === 'string'))
      ? '' : formScalar(raw);
    return `"${value.replace(/"/g, '""')}"`;
  }).join(',');
}

// The widget does not support hidden controls and logs its own schema/values.
// Hidden values stay in the React adapter, never in the widget's DOM or config.
export function visibleFormResponse(response: any, fields: NativeField[]): any {
  const ids = new Set(fields.filter(isHiddenFormField).map(field => field.id));
  if (ids.size === 0) return response;
  const scrub = (value: any): any => {
    if (Array.isArray(value)) return value
      .filter(item => !item || typeof item !== 'object' || !ids.has(item.id))
      .map(scrub);
    if (value && typeof value === 'object') return Object.fromEntries(
      Object.entries(value).filter(([key]) => !ids.has(key)).map(([key, item]) => [key, scrub(item)])
    );
    return value;
  };
  return scrub(response);
}

export function normalizeBlobReference(reference: string): string {
  if (reference.startsWith('blob:')) return reference;
  return `blob:${reference}`;
}

export function formInstance(fields: NativeField[]): string | undefined {
  const marker = getHiddenFormValues(fields).__formContinuation;
  return typeof marker === 'string' ? /^form:([^:]+):\d+$/.exec(marker)?.[1] : undefined;
}

// Generated by Copilot
// updated on: 2026-10-26 | 58 lines added | [Auto Generated] | Add workflow-agnostic hidden isolation and native quoted CSV transport | Time Taken 4 min
// Generated by Copilot
// updated on: 2026-10-26 | 18 lines added | [Auto Generated] | Reconcile editable field ids with server-owned hidden transport values | Time Taken 5 min
// Generated by Copilot
// updated on: 2026-10-26 | 8 lines added/removed | [Auto Generated] | Preserve whole blob URIs and identify ordinary form marker instances | Time Taken 2 min
